import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { DataTable, Select, Typography, useSelect } from '@chilecompra/react-kit/components'
import { formatAmount } from '../../modules/utils/formatters'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import generateYearsBetween from '../../modules/utils/years'
import { onBuyingModalitiesThunk } from './BuyingModalities.actions'
import useHorizontalBar from '../../components/Charts/HorizontalBarsChart.hooks'
import HorizontalBarsChart from '../../components/Charts/HorizontalBarsChart'
import IconButton from '../../components/Buttons/IconButton'
import Infobar from '../../components/Infobar/Infobar'
import BuyingModalitiesNoInfo from '../../components/Errors/BuyingModalitiesNoInfo'
import FichaBuyingModalities from '../../components/Infographics/FichaBuyingModalities'
import { Wrapper, GridWrapper, Link, InfoLabelXaxis } from './BuyingModalities.styles'

/**
 * Buying Modalities's container.
 */
const BuyingModalities = () => {
  const dispatch = useDispatch()
  const { latestUpdate } = useSelector(state => state.topNavbar)
  const { idEntity, name } = useSelector(state => state.buyerSection)
  const { endYear, loading: initialLoading, startYear } = useSelector(state => state.topNavbar)
  const years = generateYearsBetween(endYear, startYear)
  const inputHorizontalBarChartRef = useRef()
  const dataTableRef = useRef()
  const [showTable, setShowTable] = useState(false)
  const [expandArrow, setExpandArrow] = useState(false)
  const [fileName, setFileName] = useState(`Modalidades_Compra_${years[0].value}`)
  const {
    data: { chartStructure, csvStructure },
    fetching: loading
  } = useSelector(state => state.buyingModalities)
  const { value: yearsValue, onChange: handleChange } = useSelect({
    initialValue: years[0].value,
    changeCallback: updatedValue => {
      dataTableRef.current?.setPage(1)
      dispatch(onBuyingModalitiesThunk(idEntity, updatedValue))
      setFileName('Modalidades_Compra_' + updatedValue)
    }
  })
  const { serie: info, category: categories } = useHorizontalBar(chartStructure)
  const handleShowTable = updatedValue => () => setShowTable(updatedValue)

  const handleCaptureImage = () => exportToImage('BuyingModalities', fileName)
  const handleDownloadFile = () => exportToCsv(csvStructure, fileName)

  useEffect(() => {
    if (!idEntity || initialLoading) {
      return
    }

    dispatch(onBuyingModalitiesThunk(idEntity, years[0].value))
  }, [initialLoading, idEntity])

  return (
    <Wrapper>
      <GridWrapper container justifyContent="center">
        <GridWrapper item xs={12} md={10}>
          <Typography variant="h2" fontSize="22px" fontWeight="bold">
            Mecanismos de compra
          </Typography>
          <Typography variant="body2" fontSize="16px" lineHeight="20px" margin="16px 0 48px">
            Los ministerios, servicios y distintos organismos del Estado compran sus productos y servicios utilizando
            distintos mecanismos o procedimientos de compra.
            <br />
            <br />A continuación podrás revisar los mecanismos utilizados por {name} a través de la plataforma Mercado
            Público. A partir del año 2020 se destaca la Compra Ágil como modalidad de compra de trato directo que se
            caracteriza por ser abierta, competitiva y transparente.
            <br />
            <br />
            Según el año que consultes podrás conocer los montos transados y el número de órdenes de compra asociados a
            cada mecanismo. Recuerda que estos montos que se extraen de la orden de compra que emite una entidad en{' '}
            <Link href="https://www.mercadopublico.cl/Home">www.mercadopublico.cl</Link>, corresponden a compromisos de
            los organismos, no a pagos.
            <br />
            <br />
            La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto +
            impuestos).
          </Typography>
        </GridWrapper>
      </GridWrapper>
      <GridWrapper container alignItems="center" justifyContent="center">
        <GridWrapper container item align="left" md={8} xs={12}>
          <GridWrapper item align="left" md={3} xs={12}>
            <Select label="AÑO" options={years} value={yearsValue} onChange={handleChange} />
          </GridWrapper>
          <GridWrapper item align="left" md={5} xs={12} />
        </GridWrapper>
        <GridWrapper item align="right" md={2} xs={12} margin="6px 0 0">
          <IconButton
            singleIcon
            disabled={!showTable}
            margin="25px 10px 0 0"
            type="toggleChart"
            variant="outlined"
            onClick={handleShowTable(false)}
          />
          <IconButton
            singleIcon
            disabled={showTable}
            margin="25px 0 0"
            type="toggleTable"
            variant="outlined"
            onClick={handleShowTable(true)}
          />
        </GridWrapper>
        {showTable ? (
          <GridWrapper item xs={12} md={10}>
            <DataTable
              headers={[
                { label: 'Mecanismo de compra', value: 0 },
                { label: 'N° de órdenes de compra', value: 1 },
                { label: 'Monto total', value: 2 }
              ]}
              headersCellsProps={[{ enableSort: true }, { enableSort: true }, { enableSort: true }]}
              initialRows={6}
              loading={loading}
              records={csvStructure.slice(1)}
              recordsDecorators={[([year]) => year, ([, name]) => name, ([, , amount]) => `$${formatAmount(amount)}`]}
              ref={dataTableRef}
            />
          </GridWrapper>
        ) : chartStructure.length >= 1 ? (
          <GridWrapper item xs={12} md={10} margin="8px 0 0">
            <HorizontalBarsChart
              inputHorizontalBarChartRef={inputHorizontalBarChartRef}
              categories={categories}
              info={info}
              fileName={fileName}
              id="BuyingModalities"
            />
            <GridWrapper container justifyContent="flex-end">
              <InfoLabelXaxis label="Valores en millones de pesos (CLP)" margin="-35px 0 0" />
            </GridWrapper>
          </GridWrapper>
        ) : (
          <GridWrapper item xs={12} md={10} margin="8px 0 0">
            <BuyingModalitiesNoInfo />
          </GridWrapper>
        )}
      </GridWrapper>

      <GridWrapper container justifyContent="center">
        <GridWrapper item xs={12} md={10} margin="32px 0 0">
          <Infobar
            disableDownloadChart={showTable}
            disableDownloadCsv={!showTable}
            disableInfographic={showTable}
            expandArrow={expandArrow}
            infographic={<FichaBuyingModalities />}
            latestUpdate={latestUpdate}
            onCaptureImage={handleCaptureImage}
            onDownloadFile={handleDownloadFile}
            setExpandArrow={setExpandArrow}
          />
        </GridWrapper>
      </GridWrapper>
    </Wrapper>
  )
}

export default BuyingModalities
